import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { WetookMark } from '../components/WetookMark'
import { Button } from '../components/ui/button'
import { Textarea } from '../components/ui/textarea'
import { requestAppLicenses, getSubscription } from '../lib/api'

const MY_URL = ((import.meta as any).env?.VITE_MY_URL as string || '').replace(/\/$/, '')

/** Shown instead of the app shell when the signed-in user's ORGANIZATION
 *  doesn't have this app at all (see RequireOrgEntitlement) — distinct from
 *  AppAccessDeniedPage, which is "your org has this app but you personally
 *  aren't scoped to it." No admin can fix this from inside the app the org
 *  doesn't have.
 *
 *  Two different reasons land here, told apart via the org's Subscription
 *  status (getSubscription — distinct from the AppSubscription this whole
 *  gate is actually keyed on, see RequireOrgEntitlement):
 *    - status === "expired": the org's free trial ran out (Frame
 *      specifically — see services/screen_licenses.py) — they HAD this
 *      app, so the ask is "upgrade," not "request access."
 *    - anything else (no Subscription row, active, trialing, etc.): the
 *      org genuinely never had this app — original "request access" form,
 *      unchanged. */
export default function OrgAccessDeniedPage({ appKind, appLabel }: { appKind: string; appLabel: string }) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [trialExpired, setTrialExpired] = useState(false)

  useEffect(() => {
    let alive = true
    getSubscription()
      .then(({ data }) => { if (alive) setTrialExpired(data.status === 'expired') })
      .catch(() => { /* fall back to the "never had it" request-access copy */ })
    return () => { alive = false }
  }, [])

  const submit = async () => {
    if (sending) return
    setSending(true)
    try {
      await requestAppLicenses(appKind, { reason: reason.trim() || undefined })
      setSent(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('orgAccess.requestFailed', 'Could not send the request. Try again.'))
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-background gap-6 text-center px-6">
      <WetookMark className="w-20 h-20 text-foreground opacity-80" />
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground/60">
          {t('orgAccess.eyebrow', '403 — App Not Available')}
        </p>
        <h1 className="text-2xl font-bold text-foreground">
          {trialExpired
            ? t('orgAccess.trialTitle', 'Your free trial of {{app}} has ended', { app: appLabel })
            : t('orgAccess.title', 'Your organization doesn’t have {{app}} yet', { app: appLabel })}
        </h1>
        <p className="text-[14px] text-muted-foreground max-w-xs mx-auto leading-relaxed">
          {trialExpired
            ? t('orgAccess.trialDesc', 'Your paired screen has disconnected and this dashboard is locked. Upgrade to pick up right where you left off.')
            : t('orgAccess.desc', 'Ask us to turn it on — we’ll follow up once your request is reviewed.')}
        </p>
      </div>

      {trialExpired ? (
        MY_URL && (
          <a href={`${MY_URL}/subscription`}>
            <Button className="gap-2">{t('orgAccess.upgradeButton', 'Upgrade')}</Button>
          </a>
        )
      ) : sent ? (
        <p className="text-sm font-medium text-primary max-w-xs">
          {t('orgAccess.sent', 'Request sent — we’ll be in touch.')}
        </p>
      ) : (
        <div className="w-full max-w-xs flex flex-col gap-3">
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={t('orgAccess.reasonPlaceholder', 'What would you use it for? (optional)')}
            className="resize-none text-start"
          />
          <Button onClick={submit} disabled={sending} className="gap-2">
            {sending ? t('orgAccess.sending', 'Sending…') : t('orgAccess.requestButton', 'Request access')}
          </Button>
        </div>
      )}
    </div>
  )
}
